import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { X, FolderOpen, Trash2, Download, Plus, FileText, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { LegacyQuoteCard, Trail } from '@/types';

interface TrailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  trails: Trail[];
  activeTrail: Trail | null;
  pendingCard?: LegacyQuoteCard | null;
  onCreateTrail: (name: string) => Promise<Trail> | Trail;
  onSelectTrail: (trail: Trail | null) => void;
  onSavePending?: (trail: Trail) => Promise<unknown> | unknown;
  onRemoveFromTrail: (trailId: string, cardId: string) => Promise<unknown> | unknown;
  onDeleteTrail: (trailId: string) => Promise<unknown> | unknown;
  onExportTrail: (trail: Trail) => void;
  isLoading?: boolean;
  storageLabel?: string;
}

export function TrailsDrawer({
  isOpen,
  onClose,
  trails,
  activeTrail,
  pendingCard = null,
  onCreateTrail,
  onSelectTrail,
  onSavePending,
  onRemoveFromTrail,
  onDeleteTrail,
  onExportTrail,
  isLoading = false,
  storageLabel = 'Demo trails stay in this browser session',
}: TrailsDrawerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTrailName, setNewTrailName] = useState('');
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.2, ease: 'power2.out' }
      );
      gsap.fromTo(drawerRef.current, { x: '-100%' }, { x: 0, duration: 0.3, ease: 'power2.out' });
    }
  }, [isOpen]);

  const handleClose = () => {
    gsap.to(drawerRef.current, {
      x: '-100%',
      duration: 0.25,
      ease: 'power2.in',
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.25,
      delay: 0.05,
      ease: 'power2.in',
      onComplete: onClose,
    });
  };

  const handleCreateTrail = async () => {
    if (newTrailName.trim() && !isSubmitting) {
      setCreateError('');
      setIsSubmitting(true);
      try {
        await onCreateTrail(newTrailName.trim());
        setNewTrailName('');
        setIsCreating(false);
      } catch {
        setCreateError('Unable to create this trail. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const totalCards = trails.reduce((acc, trail) => acc + trail.cards.length, 0);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[250] bg-[#0B0D10]/50 backdrop-blur-sm"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div
        ref={drawerRef}
        className="absolute bottom-0 left-0 top-0 flex w-full max-w-md flex-col overflow-hidden bg-[#F6F5F2] shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgba(11,13,16,0.08)] bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E1062C]">
              <FolderOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-[#0B0D10]">Trails</h2>
              <p className="text-sm text-[#6B6F78]">
                {totalCards} card{totalCards !== 1 ? 's' : ''} saved
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 transition-colors hover:bg-[#F6F5F2]"
            aria-label="Close trails"
          >
            <X className="h-5 w-5 text-[#6B6F78]" />
          </button>
        </div>

        {/* Create New Trail */}
        <div className="border-b border-[rgba(11,13,16,0.08)] p-4">
          {pendingCard && (
            <div className="mb-3 rounded-xl border border-[#E1062C]/20 bg-[#E1062C]/5 p-3">
              <p className="font-medium text-[#0B0D10]">Choose where to save this receipt</p>
              <p className="mt-1 truncate text-sm text-[#6B6F78]">{pendingCard.quote}</p>
            </div>
          )}
          {isCreating ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newTrailName}
                onChange={(e) => setNewTrailName(e.target.value)}
                placeholder="Trail name..."
                className="flex-1 rounded-lg border border-[rgba(11,13,16,0.08)] bg-white px-4 py-2 text-[#0B0D10] focus:border-[#E1062C] focus:outline-none"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTrail()}
              />
              <Button
                onClick={handleCreateTrail}
                disabled={isSubmitting}
                className="bg-[#E1062C] text-white hover:bg-[#c00525]"
              >
                {isSubmitting ? 'Creating…' : 'Create'}
              </Button>
              <Button onClick={() => setIsCreating(false)} variant="outline">
                Cancel
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[rgba(11,13,16,0.2)] p-3 text-[#6B6F78] transition-colors hover:border-[#E1062C] hover:text-[#E1062C]"
            >
              <Plus className="h-5 w-5" />
              Create new trail
            </button>
          )}
          {createError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {createError}
            </p>
          )}
        </div>

        {/* Trails List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center p-6">
              <p className="text-sm text-[#6B6F78]" role="status">
                Loading your trails…
              </p>
            </div>
          ) : trails.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center p-6 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F6F5F2]">
                <FolderOpen className="h-8 w-8 text-[#6B6F78]" />
              </div>
              <p className="font-medium text-[#6B6F78]">No trails yet</p>
              <p className="mt-1 text-sm text-[#9CA3AF]">Save quote cards to build your research</p>
            </div>
          ) : (
            <div className="space-y-3 p-4">
              {trails.map((trail) => (
                <div
                  key={trail.id}
                  className={`overflow-hidden rounded-xl border bg-white transition-all ${
                    activeTrail?.id === trail.id
                      ? 'border-[#E1062C] shadow-md'
                      : 'border-[rgba(11,13,16,0.08)]'
                  }`}
                >
                  {/* Trail Header */}
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="min-w-0 flex-1 p-4 text-left"
                      onClick={() => onSelectTrail(activeTrail?.id === trail.id ? null : trail)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                              activeTrail?.id === trail.id ? 'bg-[#E1062C]' : 'bg-[#F6F5F2]'
                            }`}
                          >
                            <FolderOpen
                              className={`h-4 w-4 ${activeTrail?.id === trail.id ? 'text-white' : 'text-[#6B6F78]'}`}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-[#0B0D10]">{trail.name}</p>
                            <p className="text-xs text-[#6B6F78]">
                              {trail.cards.length} card{trail.cards.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <ChevronRight
                            className={`h-5 w-5 text-[#6B6F78] transition-transform ${
                              activeTrail?.id === trail.id ? 'rotate-90' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </button>
                    {pendingCard && onSavePending && (
                      <button
                        type="button"
                        onClick={() => onSavePending(trail)}
                        className="mr-3 flex-shrink-0 rounded-lg bg-[#E1062C] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#c00525]"
                      >
                        Save here
                      </button>
                    )}
                  </div>

                  {/* Expanded Cards */}
                  {activeTrail?.id === trail.id && (
                    <div className="border-t border-[rgba(11,13,16,0.08)]">
                      {trail.cards.length === 0 ? (
                        <p className="p-4 text-center text-sm text-[#6B6F78]">
                          No cards in this trail
                        </p>
                      ) : (
                        <div className="space-y-2 p-3">
                          {trail.cards.map((card) => (
                            <div
                              key={card.id}
                              className="group flex items-start gap-3 rounded-lg bg-[#F6F5F2] p-3"
                            >
                              <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#E1062C]" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm text-[#0B0D10]">
                                  {card.quote.substring(0, 60)}...
                                </p>
                                <p className="mt-1 text-xs text-[#6B6F78]">{card.source}</p>
                              </div>
                              <button
                                onClick={() => onRemoveFromTrail(trail.id, card.id)}
                                className="rounded-lg p-1.5 text-[#6B6F78] transition-all hover:bg-red-100 hover:text-red-600 sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
                                aria-label={`Remove receipt from ${trail.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Trail Actions */}
                      <div className="flex gap-2 border-t border-[rgba(11,13,16,0.08)] p-3">
                        <button
                          onClick={() => onExportTrail(trail)}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#F6F5F2] px-3 py-2 text-sm text-[#0B0D10] transition-colors hover:bg-[#0B0D10] hover:text-white"
                        >
                          <Download className="h-4 w-4" />
                          Export
                        </button>
                        <button
                          onClick={() => onDeleteTrail(trail.id)}
                          className="flex items-center justify-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-100"
                          aria-label={`Delete ${trail.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[rgba(11,13,16,0.08)] bg-white p-4">
          <p className="text-center text-xs text-[#6B6F78]">{storageLabel}</p>
        </div>
      </div>
    </div>
  );
}
